import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import am5index from '@amcharts/amcharts5/index';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
@Component({
  selector: 'stc-apps-triple-line-chart',
  standalone: false,
  templateUrl: './triple-line-chart.component.html',
  styleUrl: './triple-line-chart.component.scss',
})
export class TripleLineChartComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  root!: am5.Root;
  chartdiv_id = `${Math.random()}_chart_id`;
  @Input({ required: true }) data: any[] = [];
  @Input() selectedQuarter = '';
  @Input({ required: true }) colors: string[] = [];
  private staticTooltip: am5.Tooltip | undefined;
  private cursorLine: am5.Line | undefined;
  ngOnDestroy(): void {
    // 4. Clean up when component is removed
    if (this.root) {
      this.root.dispose();
    }
    am5.array.each(am5.registry.rootElements, (root) => {
      if (root && root.dom.id === this.chartdiv_id) {
        root.dispose();
      }
    });
    if (this.staticTooltip) {
      this.staticTooltip.dispose();
    }
  }
  ngAfterViewInit() {
    this.tripleLineChartData();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && !changes['data'].firstChange && this.root) {
      this.tripleLineChartData();
    }
    if (changes['selectedQuarter'] && this.root && this.data.length > 0) {
      this.updateStaticTooltip();
    }
  }

  private updateStaticTooltip() {
    if (!this.root || !this.selectedQuarter || this.data.length === 0) return;

    const chart = this.root.container.children.getIndex(0) as am5xy.XYChart;
    let xAxis: am5xy.CategoryAxis<am5xy.AxisRenderer> | undefined;
    if (chart && chart.xAxes) {
      xAxis = chart.xAxes.getIndex(0) as am5xy.CategoryAxis<am5xy.AxisRenderer>;
    }
    if (!chart || !xAxis) return;

    const quarterIndex = this.data.findIndex(
      (item) => item.name === this.selectedQuarter
    );
    if (quarterIndex === -1) return;

    const quarterData = this.data[quarterIndex];

    // 1. Calculate X position (Always use 0.5 for center if series locationX is 0.5)
    const xPosition = xAxis
      .get('renderer')
      .positionToCoordinate(xAxis.indexToPosition(quarterIndex, 0.5));

    // Clean up
    if (this.cursorLine) this.cursorLine.dispose();
    if (this.staticTooltip) this.staticTooltip.dispose();

    // 2. Draw dashed line (Keep in plotContainer)
    // this.cursorLine = chart.plotContainer.children.push(
    //   am5.Line.new(this.root, {
    //     x: xPosition,
    //     y: 0,
    //     stroke: am5.color(0x94a3b8),
    //     strokeWidth: 1,
    //     strokeDasharray: [4, 4],
    //     height: chart.plotContainer.height(),
    //   })
    // );
    const range = xAxis.createAxisRange(
      xAxis.makeDataItem({
        category: this.selectedQuarter,
      })
    );

    range.get('grid')?.setAll({
      visible: true,
      stroke: am5.color(0x94a3b8),
      strokeDasharray: [4, 4],
      strokeWidth: 1,
    });

    // 3. Create Tooltip with BOUNDS
    this.staticTooltip = am5.Tooltip.new(this.root, {
      getFillFromSprite: false,
      autoTextColor: false,
      pointerOrientation: 'vertical',

      // [FIX] This tells the tooltip: "Don't go outside the chart area"
      // It will automatically flip left if it hits the right edge (Q4)
      bounds: {
        left: 0,
        top: 0,
        right: this.root.container.width(),
        bottom: this.root.container.height(),
      },

      background: am5.RoundedRectangle.new(this.root, {
        fill: am5.color(0xffffff),
        stroke: am5.color(0xd1d5db),
        strokeWidth: 1,
        shadowBlur: 10,
        shadowOpacity: 0.1,
      }),
      html: `<div style="padding:5px; display: flex; align-items: center; font-size: 14px; gap: 15px;">
      <div style="color: ${this.colors[0]};font-family:STCForwardFont;"><span style="width: 10px; height: 10px; background-color: ${this.colors[0]}; border-radius: 50%; display: inline-block;"></span> ${quarterData.Acceptable}%</div>
      <div style="color: ${this.colors[1]};font-family:STCForwardFont;"><span style="width: 10px; height: 10px; background-color: ${this.colors[1]}; border-radius: 50%; display: inline-block;"></span> ${quarterData.Tolerable}%</div>
      <div style="color: ${this.colors[2]};font-family:STCForwardFont;"><span style="width: 10px; height: 10px; background-color: ${this.colors[2]}; border-radius: 50%; display: inline-block;"></span> ${quarterData.Unacceptable}%</div>
    </div>`,
      paddingBottom: 0,
      paddingTop: 0,
      paddingLeft: 10,
      paddingRight: 10,
      marginBottom: 0,
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
    });

    // 4. Add to Root Container (avoids clipping)
    this.root.container.children.push(this.staticTooltip);

    // 5. Coordinate Conversion
    // Convert local plot coordinates (where the line is) to global root coordinates (where the tooltip is)
    const plotPoint = { x: xPosition, y: 0 };
    const globalPoint = chart.plotContainer.toGlobal(plotPoint);
    const rootPoint = this.root.container.toLocal(globalPoint);

    this.staticTooltip.set('pointTo', rootPoint);
    this.staticTooltip.show();
  }

  tripleLineChartData() {
    if (this.root) {
      this.root.dispose();
    }
    this.root = am5.Root.new(this.chartdiv_id);
    this.root.setThemes([am5themes_Animated.new(this.root)]);
    const chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        paddingLeft: 0,
      })
    );
    const colors2: any[] = [];
    this.colors.forEach((color, index) => {
      colors2.push(am5.color(color));
    });
    chart.get('colors')?.set('colors', colors2);
    if (this.root._logo) {
      this.root._logo.dispose();
    }
    // const date = new Date();
    // date.setHours(0, 0, 0, 0);
    // let value = 100;
    // function generateData() {
    //   value = Math.round(Math.random() * 10 - 5 + value);
    //   am5.time.add(date, 'day', 1);
    //   return {
    //     date: date.getTime(),
    //     value: value,
    //   };
    // }
    // function generateDatas(count: number) {
    //   const data = [];
    //   for (let i = 0; i < count; ++i) {
    //     data.push(generateData());
    //   }
    //   return data;
    // }
    const xRenderer = am5xy.AxisRendererX.new(this.root, {
      minGridDistance: 50,
      visible: true,
      strokeOpacity: 1,
      strokeWidth: 2,
      stroke: am5.color('#E2E8F0'),
      // tooltip: am5.Tooltip.new(this.root, {
      //   getFillFromSprite: false,
      //   pointerOrientation: 'vertical',
      //   getLabelFillFromSprite: false,
      // }),
    });
    // console.log(this.colors);
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: 'name',
        renderer: xRenderer,
        visible: true,
        tooltip: am5.Tooltip.new(this.root, {
          // getFillFromSprite: false,
          // pointerOrientation: 'vertical',
          // getLabelFillFromSprite: false,
          // labelText: 'xvsdfsf {categoryX}',
          // Position tooltip above the cursor
          // dy: -242, // Move tooltip upward
          layout: this.root.horizontalLayout,
          labelHTML: `<div style = "padding: 5px;display: flex; align-items: center; font-size: 14px;justify-content:space-between">
    <div style="color: ${this.colors[0]};font-family:STCForwardFont;padding-right:20px"><span style = "width: 13px; height: 13px; position:relative; top:1px; background-color: ${this.colors[0]}; border-radius: 50%; display: inline-block; margin-right: 2px;"></span> {dataItem.dataContext.Acceptable}%</div>
    <div style="color: ${this.colors[1]};font-family:STCForwardFont;padding-right:20px"><span style = "width: 13px; height: 13px; position:relative; top:1px; background-color: ${this.colors[1]}; border-radius: 50%; display: inline-block; margin-right: 2px;"></span> {dataItem.dataContext.Tolerable}%</div>
    <div style="color: ${this.colors[2]};font-family:STCForwardFont;padding-right:0px"><span style = "width: 13px; height: 13px; position:relative; top:1px; background-color: ${this.colors[2]}; border-radius: 50%; display: inline-block; margin-right: 2px;"></span> {dataItem.dataContext.Unacceptable}%</div>
  </div>`,
          // width: 200,
          paddingBottom: 0,
          paddingTop: 0,
          paddingLeft: 10,
          paddingRight: 10,
          marginBottom: 0,
          marginTop: 0,
          marginLeft: 0,
          marginRight: 0,
          background: am5.RoundedRectangle.new(this.root, {
            fill: am5.color(0xffffff),
            fillOpacity: 1,
            stroke: am5.color(0xd1d5db),
            strokeWidth: 1,
            shadowBlur: 10,
            shadowOpacity: 0.1,
          }),
        }),
        // tooltip: am5.Tooltip.new(this.root, {}),
      })
    );
    xAxis.setAll({
      startLocation: 0.4,
      endLocation: 0.7,
    });

    const yRenderer = am5xy.AxisRendererY.new(this.root, {
      visible: true,
      strokeOpacity: 1,
      strokeWidth: 2,
      stroke: am5.color('#E2E8F0'),
    });
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        maxDeviation: 1,
        renderer: yRenderer,
        min: 0,
      })
    );
    yRenderer.grid.template.setAll({
      visible: false,
    });
    const range = xAxis.createAxisRange(
      xAxis.makeDataItem({
        category: this.selectedQuarter,
      })
    );

    // إظهار خط الـ range فقط
    range.get('grid')?.setAll({
      visible: true,
      stroke: am5.color(0x94a3b8),
      strokeDasharray: [4, 4],
      strokeWidth: 1,
      strokeOpacity: 1,
      location: 0.5,
    });
    xRenderer.grid.template.setAll({
      visible: false,
    });
    const series1: any = chart.series.push(
      am5xy.SmoothedXLineSeries.new(this.root, {
        name: 'Series',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'Acceptable',
        tension: 0.5,
        categoryXField: 'name',
        // tooltip: am5.Tooltip.new(this.root, {
        //   labelText: 'Acceptable in {categoryX}: {valueY}%',
        // }),
      })
    );
    const series2: any = chart.series.push(
      am5xy.SmoothedXLineSeries.new(this.root, {
        name: 'Series',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'Tolerable',
        tension: 0.5,
        categoryXField: 'name',
        // tooltip: am5.Tooltip.new(this.root, {
        //   labelText: 'Tolerable in {categoryX} : {valueY}%',
        // }),
      })
    );
    const series3: any = chart.series.push(
      am5xy.SmoothedXLineSeries.new(this.root, {
        name: 'Series',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'Unacceptable',
        tension: 0.5,
        categoryXField: 'name',
        // tooltip: am5.Tooltip.new(this.root, {
        //   labelText: 'Unacceptable in {categoryX} : {valueY}%',
        // }),
      })
    );
    const series4 = chart.series.push(
      am5xy.SmoothedXLineSeries.new(this.root, {
        name: 'Series',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'Unclassified',
        tension: 0.5,
        categoryXField: 'name',
        // tooltip: am5.Tooltip.new(this.root, {
        //   labelText: 'Unclassified in {categoryX} : {valueY}%',
        // }),
      })
    );

    // Call this function if a quarter is selected
    if (this.selectedQuarter) {
      // Use setTimeout to ensure chart is rendered first
      setTimeout(() => {
        this.updateStaticTooltip();
      }, 100);
    }

    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(this.root, {
        behavior: 'none',
        xAxis: xAxis,
        snapToSeries: [series1, series2, series3],
      })
    );
    cursor.lineY.set('visible', false);
    // cursor.lineX.set('visible', false);
    cursor.lineX.setAll({
      visible: true,
      stroke: am5.color(0x94a3b8),
      strokeDasharray: [4, 4], // Creates the dashed effect
    });
    series1.setAll({
      locationX: 0.5,
      // seriesTooltipTarget: 'xAxis',
      // tooltipText: '[#22c55e]●[/] [bold]{valueY}%[/]  ', // Added spaces for horizontal padding
    });

    // Update series 2 (Yellow)
    series2.setAll({
      locationX: 0.5,
      // seriesTooltipTarget: 'xAxis',
      // tooltipText: '[#eab308]●[/] [bold]{valueY}%[/]  ',
    });

    // Update series 3 (Red)
    series3.setAll({
      locationX: 0.5,
      // seriesTooltipTarget: 'xAxis',
      // tooltipText: '[#dc2626]●[/] [bold]{valueY}%[/]',
    });

    series4.setAll({
      locationX: 0.5,
    });
    chart.setAll({
      paddingLeft: 0,
      paddingRight: 0,
    });
    series1.strokes.template.setAll({
      strokeWidth: 6,
      lineCap: 'round',
    });
    series2.strokes.template.setAll({
      strokeWidth: 6,
      lineCap: 'round',
      // strokeLinecap: 'round',
    });
    series3.strokes.template.setAll({
      strokeWidth: 6,
      lineCap: 'round',
      // strokeLinecap: 'round',
    });
    series4.strokes.template.setAll({
      strokeWidth: 6,
      lineCap: 'round',
      // strokeLinecap: 'round',
    });
    // series1.set('stroke', am5.color('#22C55E'));
    // series2.set('stroke', am5.color('#EAB308'));
    // series3.set('stroke', am5.color('#EF4444'));
    // series.fills.template.setAll({
    //   visible: true,
    //   fillOpacity: 0.2,
    // });
    // series.bullets.push(function () {
    //   return am5.Bullet.new(root, {
    //     locationY: 0,
    //     sprite: am5.Circle.new(root, {
    //       radius: 4,
    //       stroke: root.interfaceColors.get('background'),
    //       strokeWidth: 2,
    //       fill: series.get('fill'),
    //     }),
    //   });
    // });
    // chart.set(
    //   'scrollbarX',
    //   am5.Scrollbar.new(root, {
    //     orientation: 'horizontal',
    //   })
    // );
    // const data = [
    //   { name: 'Q1', value1: 65, value2: 45, value3: 35 },
    //   { name: 'Q2', value1: 55, value2: 28, value3: 15 },
    //   { name: 'Q3', value1: 72, value2: 20, value3: 12 },
    //   { name: 'Q4', value1: 77, value2: 16, value3: 8 },
    // ];
    const data = this.data;
    xAxis.data.setAll(data);
    series1.data.setAll(data);
    series1.appear(1000);
    series2.data.setAll(data);
    series2.appear(1000);
    series3.data.setAll(data);
    series3.appear(1000);
    chart.appear(1000, 100);
    series4.data.setAll(data);
    series4.appear(1000);
    chart.appear(1000, 100);
  }
}
